<script setup lang="ts">
import StepView from 'modules/Step/views/StepView/StepView.vue';
import StepTheoryView from 'modules/Step/views/StepTheoryView/StepTheoryView.vue';
import CompletedStepBanner from 'shared/components/CompletedStepBanner/CompletedStepBanner.vue';

import { useRoute, useRouter } from 'vue-router';
import { useStepData } from 'modules/Step/useStepData';
import { useUserSectionData } from '../useUserSectionData';
import { useSectionStepSeed } from '../../composables/useSectionStepSeed';
import type { ComputedRef } from 'vue';
import { computed, inject, onMounted, ref, watchEffect } from 'vue';
import { useTermsOfTransition } from 'shared/composables/useTermsOfTransition';
import { resolveStepTransition } from 'modules/Section/utilities/stepTransitionResolver';
import { stepRetryFeedback } from 'modules/Section/utilities/stepRetryFeedback';
import type { TermsOfTransition } from 'types/course/termsOfTransition';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
import { useGlobalStore } from 'src/stores/global';
import { logger } from 'src/shared/utilities/logger';
import type { UserRole } from 'src/types/User';

const globalStore = useGlobalStore();
// const { listen } = useEvents();

const $q = useQuasar();
const route = useRoute();
const router = useRouter();
const currentUserRole =
  inject<ComputedRef<UserRole | undefined>>('currentUserRole');
const isSchoolStudent = computed(() => currentUserRole?.value === 'student');
const {
  params: { userSectionDocId, userStepId, stepDocId, userCourseDocId },
} = route;

interface Props {
  courseSectionTermsOfTransitionAction?: string | null | undefined;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  'section:completed': [score: number, timeAccumulator: number];
  'section:transition': [];
}>();

const stepView = ref<typeof StepView | null>(null);

const currentUserId = inject('currentUserId') as ComputedRef<number>;

const {
  getUserStepData,
  replaceMissingUserStep,
  updateUserSectionRepo,
  updateUserSectionStrapi,
  currentSectionProgress,
  nextSectionStep,
  generateUserStep,
  getSectionStepTermsOfTransition,
  resetSectionStepProgress,
  setSectionUserStepDocId,
  resetUserSectionProgress,
  sectionScore,
  accumulatedTime,
  userSectionByCurrentId,
  isSctionCompletionDate,
  sectionStepsCount,
  sectionSteps,
} = useUserSectionData({ userSectionDocId: userSectionDocId as string });

// P4-5: the section's copy of the step gives its kind before the step GET.
useSectionStepSeed({ stepDocId: stepDocId as string, sectionSteps });
const { stepType } = useStepData(stepDocId as string);

async function ensureUserStepRouteParam() {
  const currentStepDocId = stepDocId as string | undefined;
  if (!currentStepDocId || !currentUserId?.value) return;

  const parsedUserStepId = Number(userStepId);
  if (userStepId && Number.isFinite(parsedUserStepId) && parsedUserStepId > 0) {
    return;
  }

  const generatedUserStepId = await generateUserStep(currentStepDocId);

  if (!generatedUserStepId) return;

  await openUserStep(generatedUserStepId, currentStepDocId);
}

/** The same step, with the given user-step in the URL. */
async function openUserStep(nextUserStepId: number, currentStepDocId: string) {
  await router.replace({
    name: userCourseDocId ? 'course.step' : 'section.steps.step',
    params: {
      ...(route.params as Record<string, string | number>),
      userStepId: String(nextUserStepId),
      stepDocId: currentStepDocId,
    },
  });
}

globalStore.clearHeaderActions();
globalStore.setTotal(undefined);

globalStore.setHeaderActions([
  'action:instruction',
  'action:reset',
  'action:help',
  'action:interim',
]);

const nextRoute = async () => {
  const nextStepDocId = nextSectionStep?.value?.documentId;
  if (!nextStepDocId) return undefined;

  const nextUserStepId = await generateUserStep(nextStepDocId);

  if (!nextUserStepId) {
    return undefined;
  }

  if (userCourseDocId) {
    // If the user is in a course, go to the next section
    return {
      name: 'course.step',
      params: {
        userCourseDocId,
        userSectionDocId,
        userStepId: nextUserStepId,
        stepDocId: nextStepDocId,
      },
    };
  }

  return {
    name: 'section.steps.step',
    params: {
      userSectionDocId,
      userStepId: nextUserStepId,
      stepDocId: nextStepDocId,
    },
  };
};

const currentStepProgress = computed(() =>
  currentSectionProgress.value?.steps?.find((s) => s.step_doc_id === stepDocId),
);

// Get the user section data
onMounted(async () => {
  await ensureUserStepRouteParam();

  const parsedUserStepId = Number(route.params.userStepId);
  if (!Number.isFinite(parsedUserStepId) || parsedUserStepId <= 0) return;

  // The user-step was deleted (a reset, an old link): replace it and reopen.
  if ((await getUserStepData(parsedUserStepId)) === 'missing' && stepDocId) {
    const replacementId = await replaceMissingUserStep(stepDocId as string);
    if (replacementId) await openUserStep(replacementId, stepDocId as string);
  }
});

const sectionTermsOfTransition = computed(() =>
  useTermsOfTransition(getSectionStepTermsOfTransition(stepDocId as string)),
);

const isPassed = (score: number) => {
  return sectionTermsOfTransition.value?.isPassed(score);
};
const isPassedStep = computed(() => {
  return (
    currentStepProgress.value?.completion_date ||
    currentStepProgress.value?.isSkippedFilter ||
    isPassed(currentStepProgress.value?.score ?? 0)
  );
});

// A skipped filter (nothing to correct) has no score of its own; resolving the
// CMS rules against its default 0 would pick the low band (e.g. 0-30 →
// Redo_Current) and trap the learner on an empty step, so it always advances.
const termsOfTransition = ref<TermsOfTransition | null | undefined>();
watchEffect(() => {
  if (!sectionTermsOfTransition?.value) return;
  termsOfTransition.value = resolveStepTransition({
    termsOfTransition: sectionTermsOfTransition.value.termsOfTransition,
    score: currentStepProgress.value?.score ?? 0,
    isSkippedFilter: !!currentStepProgress.value?.isSkippedFilter,
  });
});

async function getOrCreateSectionStepProgress() {
  if (!currentSectionProgress.value) return null;

  let sectionStepProgress = currentSectionProgress.value?.steps?.find(
    (s) => s.step_doc_id === stepDocId,
  );

  if (sectionStepProgress) return sectionStepProgress;

  if (userStepId) {
    await setSectionUserStepDocId(stepDocId as string, +userStepId);
    sectionStepProgress = currentSectionProgress.value?.steps?.find(
      (s) => s.step_doc_id === stepDocId,
    );
  }

  return sectionStepProgress ?? null;
}

// While Redo_Current runs, the reset user-step's new start_date must not
// reach the section entry (D-9).
let isRedoing = false;

async function updateStepStartDate(dateTime: string) {
  if (isRedoing) return;
  // Guard: If section progress hasn't loaded yet, skip silently
  if (!currentSectionProgress.value) {
    console.warn(
      'SectionStepView: currentSectionProgress not loaded yet, skipping start date update',
    );
    return;
  }

  // 1. find (or create) the step progress entry
  const sectionStepProgress = await getOrCreateSectionStepProgress();
  if (!sectionStepProgress) {
    console.warn(
      'SectionStepView: Step Progress Not Found for start date update',
    );
    return;
  }

  // 2. update with the values — the first start_date is kept (D-9)
  if (!dateTime || sectionStepProgress.start_date) return;
  Object.assign(sectionStepProgress, { start_date: dateTime });

  // 3. update the repo and strapi
  updateUserSectionRepo({ progress: currentSectionProgress.value });
  void updateUserSectionStrapi({ progress: currentSectionProgress.value });
}

function updateUserSectionProgressCompletion({
  score,
  timeAccumulator,
}: {
  score?: number;
  timeAccumulator?: number;
} = {}) {
  void (async () => {
    // Guard: If section progress hasn't loaded yet (page refresh race condition), skip silently
    if (!currentSectionProgress.value) {
      console.warn(
        'SectionStepView: currentSectionProgress not loaded yet, skipping update',
      );
      return;
    }

    // 1. find (or create) the step progress entry
    const sectionStepProgress = await getOrCreateSectionStepProgress();
    if (!sectionStepProgress) {
      console.warn('SectionStepView: Step Progress Not Found', {
        stepDocId,
        currentProgressSteps: currentSectionProgress.value?.steps?.map(
          (s) => s.step_doc_id,
        ),
      });
      return;
    }

    // 2. update with the values
    if (score !== undefined) Object.assign(sectionStepProgress, { score });

    // If theory set the completion date, else If the score is passed, set the completion date
    if (
      stepType.value === 'theory' ||
      (score !== undefined && isPassed(score))
    ) {
      Object.assign(sectionStepProgress, {
        completion_date: new Date().toISOString(),
      });
    }

    if (timeAccumulator)
      Object.assign(sectionStepProgress, { time_accumulator: timeAccumulator });

    // 3. update the repo and strapi
    updateUserSectionRepo({ progress: currentSectionProgress.value });
    void updateUserSectionStrapi({ progress: currentSectionProgress.value });

    // Keep section card metrics in sync during in-progress attempts.
    const partialScore = Math.round(sectionScore.value ?? 0);
    const partialAccumulatedTime = accumulatedTime.value ?? 0;
    updateUserSectionRepo({
      score: partialScore,
      time_accumulator: partialAccumulatedTime,
    });
    void updateUserSectionStrapi({
      score: partialScore,
      time_accumulator: partialAccumulatedTime,
    });
  })();
}

async function goToNextRoute() {
  const nextRouteObj = await nextRoute();
  if (nextRouteObj) {
    void router.push(nextRouteObj);
    return;
  }
  await completeSection();
}

// Reached either by running out of steps or by an End_All rule, which ends the
// section from wherever the learner is standing.
async function completeSection() {
  // No next step can mean "last step done" or "steps not loaded". Only the
  // first is a completion — without this the section gets stamped complete
  // off empty state, the same way courses were.
  if (sectionStepsCount.value === 0) return;

  const scoreValue: number = Math.round(sectionScore?.value ?? 0);
  const accumulatedTimeValue: number = accumulatedTime?.value ?? 0;

  const updateData = {
    score: scoreValue,
    time_accumulator: accumulatedTimeValue,
    completion_date: new Date().toISOString(),
  };
  if (!isSctionCompletionDate.value) {
    emits('section:completed', scoreValue, accumulatedTimeValue);
    await updateUserSectionStrapi(updateData);
    updateUserSectionRepo(updateData);

    if (
      userCourseDocId &&
      props.courseSectionTermsOfTransitionAction === 'Redo_Current'
    ) {
      return;
    }
  }

  // 4. add new quasar dialog to inform the user that the section is completed
  // wait for the user to click on the button
  $q.dialog({
    title: t('SECTION_COMPLETED') + ' 🎉',
    message: t('YOU_HAVE_COMPLETED_THIS_SECTION_WOULD_YOU_LIKE_TO_PROCEED'),
    persistent: true,
    ok: {
      label: 'CONTINUE',
      color: 'secondary',
      'text-color': 'dark',
      unelevated: true,
      class: 'text-bold q-mx-auto rounded-borders-sm',
      padding: 'sm lg',
    },
    class: 'rounded-borders-lg shadow-primary-sm text-center',
  }).onOk(() => {
    // 1. Go to next section
    // 5. Go to next section on course
    if (userCourseDocId) {
      emits('section:transition');
    } else {
      // if My Sections go sections page
      void router.push({
        name: isSchoolStudent.value ? 'sections.my.student' : 'sections',
      });
    }
  });
}

const isShownCompletionBanner = ref(true);
//  'Redo_Current' | 'Go_To_Next' | 'Go_To_Previous' | 'Skip' | 'End_All'
async function handleStepCompletion() {
  // Don't hide banner immediately - only hide when navigating away

  // Leaving the step: its last progress save must land first (capped).
  // (Redo_Current's reset drains the saves itself.)
  if (termsOfTransition.value?.action !== 'Redo_Current') {
    await stepView.value?.awaitSaves?.();
  }

  if (
    stepType.value === 'theory' ||
    termsOfTransition.value?.action === 'Go_To_Next' ||
    // Skip is a passing action: it moves the learner on, it does not repeat.
    termsOfTransition.value?.action === 'Skip'
  ) {
    isShownCompletionBanner.value = false; // Hide before navigation
    void goToNextRoute();
  } else if (termsOfTransition.value?.action === 'Redo_Current') {
    isShownCompletionBanner.value = false; // Hide before reset
    // D-9: the step keeps its place and start_date in the section progress.
    // The section write follows the user-step reset, from the entry as it was.
    const steps = currentSectionProgress.value?.steps ?? [];
    const index = steps.findIndex((s) => s.step_doc_id === stepDocId);
    const original =
      index >= 0 ? { index, step: { ...steps[index]! } } : undefined;
    isRedoing = true;
    try {
      await stepView.value?.resetStep();
      await resetSectionStepProgress(
        stepDocId as string,
        userStepId ? +userStepId : 0,
        original,
      );
    } finally {
      isRedoing = false;
    }
    isShownCompletionBanner.value = true; // Show again for next completion
    await stepView.value?.playQuestion();
  } else if (termsOfTransition.value?.action === 'End_All') {
    // End the section here: the remaining steps are not played.
    isShownCompletionBanner.value = false;
    await completeSection();
  } else if (termsOfTransition.value?.action === 'Go_To_Previous') {
    isShownCompletionBanner.value = false; // Hide before navigation
    // 1. Get the last 2 steps from progress
    const lastSteps = currentSectionProgress.value?.steps?.slice(-2) ?? [];
    if (lastSteps.length === 0) return;
    const lastStepIds = lastSteps.map((s) => s.user_step_id);

    // 2. Reset the steps from the progress
    await resetUserSectionProgress(lastStepIds);

    // 3. Go to the last step
    await goToNextRoute();
  } else {
    // The branches above cover the whole CMS enum, so this is an action the
    // app does not know or a rule that resolved to nothing. Moving on is the
    // only outcome that cannot strand the learner on a banner whose button
    // does nothing.
    logger.log(
      'warn',
      'SectionStepView: unhandled terms of transition action',
      {
        module: 'Section',
        action: 'step-completion',
        transitionAction: termsOfTransition.value?.action ?? 'unresolved',
        stepDocId,
      },
    );
    isShownCompletionBanner.value = false;
    void goToNextRoute();
  }
}

// Default text for a retry rule (Go_To_Previous / Redo_Current) left without
// result_feedback in the CMS: the score, what moving on needs, what comes next.
const retryFeedback = computed(() =>
  stepRetryFeedback({
    action: termsOfTransition.value?.action,
    score: currentStepProgress.value?.score ?? 0,
    terms: sectionTermsOfTransition.value?.termsOfTransition,
    isFirstStep: sectionSteps.value?.[0]?.documentId === stepDocId,
    t,
  }),
);

const completionBannerMessage = computed(() => {
  if (isSkippedStep.value) {
    return t('YOU_DID_GOOD_ON_PREVIOUS_STEPPS_THIS_FILTER_STEP_IS_NOT_NEEDED');
  }

  if (termsOfTransition.value?.result_feedback) {
    return termsOfTransition.value.result_feedback;
  }

  if (retryFeedback.value) return retryFeedback.value.message;

  if (
    termsOfTransition.value?.result_feedback === null &&
    termsOfTransition.value?.action !== 'Go_To_Next'
  ) {
    logger.log(
      'warn',
      'SectionStepView: termsOfTransition.result_feedback is explicitly null',
    );
    return t('NO_FEEDBACK_AVAILABLE');
  }

  return t('COMPLETED_STEP_BANNER_MESSAGE');
});

const completionBannerTitle = computed(() => {
  if (isSkippedStep.value) {
    return t('FILTER_STEP');
  }

  if (termsOfTransition.value?.result_feedback) {
    return termsOfTransition.value.result_feedback;
  }

  if (retryFeedback.value) return retryFeedback.value.title;

  if (
    termsOfTransition.value?.result_feedback === null &&
    termsOfTransition.value?.action !== 'Go_To_Next'
  ) {
    logger.log(
      'warn',
      'SectionStepView: termsOfTransition.result_feedback is explicitly null',
    );
    return t('COMPLETION_FEEDBACK');
  }

  return t('COMPLETED_STEP_BANNER_TEXT');
});

const isSkippedStep = computed(
  () => !!currentStepProgress.value?.isSkippedFilter,
);

async function handleSkippedFilter() {
  // A correction step with nothing to fix is skipped before it ever starts, so
  // its progress entry usually does not exist yet. Looking it up without
  // creating it dropped the skip flag silently, and the step then counted as a
  // plain unanswered 0 in the section average.
  const sectionStepProgress = await getOrCreateSectionStepProgress();
  if (!sectionStepProgress) return;
  Object.assign(sectionStepProgress, { isSkippedFilter: true });

  // 3. update the repo and strapi
  updateUserSectionRepo({ progress: currentSectionProgress.value });
  await updateUserSectionStrapi({ progress: currentSectionProgress.value });
}
</script>

<template>
  <div class="col q-pb-sm full-height">
    <!-- Loading state while data is being fetched -->
    <div
      v-if="!userSectionByCurrentId"
      class="flex flex-center full-height"
    >
      <q-spinner-gears
        size="80px"
        color="primary"
      />
    </div>

    <StepView
      v-else-if="stepType !== 'theory'"
      ref="stepView"
      :terms-of-transitions="
        getSectionStepTermsOfTransition(stepDocId as string)
      "
      :user-section-id="userSectionByCurrentId?.id"
      :next-section-step-doc-id="nextSectionStep?.documentId ?? undefined"
      @step:start="updateStepStartDate"
      @step:complete="updateUserSectionProgressCompletion"
      @action:completion="handleStepCompletion"
      @step:skipped-filter="handleSkippedFilter"
    >
      <template #completionBanner>
        <CompletedStepBanner
          v-show="isShownCompletionBanner"
          :color="isPassedStep ? 'positive-light' : 'negative-light'"
          :action-btn-label="
            termsOfTransition?.action?.toUpperCase() ?? 'NEXT_STEP'
          "
          @completion-action="handleStepCompletion"
        >
          <template #header>
            {{ completionBannerTitle }}
          </template>
          <template #message>
            {{ completionBannerMessage }}
          </template>
        </CompletedStepBanner>
      </template>
    </StepView>
    <StepTheoryView
      v-else-if="userSectionByCurrentId"
      :is-completed="!!currentStepProgress?.completion_date"
      @step:start="updateStepStartDate"
      @step:complete="updateUserSectionProgressCompletion"
      @action:completion="handleStepCompletion"
    />
  </div>
</template>
